/**
 * The header control for moving between the cases the simulator was opened with. Purely
 * presentational: it is handed the cases and the loaded one and reports a choice, so
 * whatever opened the simulator decides what is on offer.
 */

import type { ReactNode } from 'react';
import { Check, ChevronDown, FlaskConical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { KindBadge } from '@/modules/back-testing/kind-badge';
import { ScenarioTypePeek } from './scenario-type-peek';
import type { ScenarioCase } from './scenario-case';

/**
 * Long enough that crossing the chip costs nothing: the tooltip mounts its content only when
 * it opens, so the type is fetched only on a real pause.
 */
const PEEK_DELAY_MS = 500;

/** Holding the pointer on the chip explains what the run is supposed to do; a case with no type is returned untouched. */
const WithTypePeek = ({ scenarioCase, children }: { scenarioCase: ScenarioCase | null; children: ReactNode }) => {
  if (!scenarioCase?.sopId || !scenarioCase.scenarioTypeId) return <>{children}</>;
  return (
    <Tooltip delayDuration={PEEK_DELAY_MS}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side='bottom' align='start' className='max-w-none p-3'>
        <ScenarioTypePeek sopId={scenarioCase.sopId} scenarioTypeId={scenarioCase.scenarioTypeId} />
      </TooltipContent>
    </Tooltip>
  );
};

/** "Scenario" leads, so the chip reads as the case it is and not as the customer's opening words. */
const CaseLabel = ({ scenarioCase, position }: { scenarioCase: ScenarioCase; position?: string }) => (
  <>
    <span className='shrink-0 font-semibold'>
      Scenario{position && <span className='ml-1 tabular-nums'>{position}</span>}
    </span>
    <span aria-hidden='true' className='h-3 w-px shrink-0 bg-indigo-200' />
    <span className='truncate font-normal'>{scenarioCase.name}</span>
    <KindBadge kind={scenarioCase.kind} />
  </>
);

interface ScenarioCaseLoaderProps {
  cases: ScenarioCase[];
  /** Index of the case on screen, or null when the simulator was reset. */
  activeIndex: number | null;
  onSelect: (index: number) => void;
}

export const ScenarioCaseLoader = ({ cases, activeIndex, onSelect }: ScenarioCaseLoaderProps) => {
  if (!cases.length) return null;
  const active = activeIndex === null ? null : cases[activeIndex];

  // One case and nowhere to move: a menu offering the only choice already made is not a control.
  if (cases.length === 1) {
    return (
      <WithTypePeek scenarioCase={active}>
        <span className='flex max-w-88 items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700'>
          <FlaskConical className='size-3.5 shrink-0' />
          {active ? <CaseLabel scenarioCase={active} /> : <span className='truncate'>Scenario</span>}
        </span>
      </WithTypePeek>
    );
  }

  return (
    <DropdownMenu>
      {/* The peek wraps the trigger, not the reverse: the menu trigger cannot hand its ref to a tooltip root. */}
      <WithTypePeek scenarioCase={active}>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            className='flex max-w-88 items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100'
          >
            <FlaskConical className='size-3.5 shrink-0' />
            {active && activeIndex !== null ? (
              <CaseLabel scenarioCase={active} position={`${activeIndex + 1}/${cases.length}`} />
            ) : (
              <span className='shrink-0'>Load scenario ({cases.length})</span>
            )}
            <ChevronDown className='size-3.5 shrink-0' />
          </button>
        </DropdownMenuTrigger>
      </WithTypePeek>
      <DropdownMenuContent align='end' className='max-h-[70vh] w-80 overflow-y-auto'>
        <DropdownMenuLabel className='text-[11px] font-normal text-muted-foreground'>
          {cases.length} scenario{cases.length === 1 ? '' : 's'} in this set
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {cases.map((scenarioCase, index) => (
          <DropdownMenuItem
            key={`${scenarioCase.name}-${index}`}
            onSelect={() => onSelect(index)}
            className='items-start gap-2 text-xs'
          >
            <span className='w-4 shrink-0 pt-px text-[11px] tabular-nums text-muted-foreground'>{index + 1}</span>
            <span className='min-w-0 flex-1 whitespace-normal wrap-break-word'>{scenarioCase.name}</span>
            <KindBadge kind={scenarioCase.kind} />
            {index === activeIndex && <Check className='mt-px size-3.5 shrink-0 text-primary' />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
